import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo, ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { useTenantSelection } from '../TenantSelectionProvider';
import { useToast } from '../ToastProvider/ToastContext';
import api from '../../components/services/apiService';
import DownloadStatus from '../../components/DownloadStatus/DownloadStatus';

interface ReportJob {
  correlationId: string;
  message: string;
  isSuccessful: boolean;
  result?: string;
  completedAt?: string;
  metadata?: {
    reportType?: string;
    [key: string]: any;
  };
}

interface SignalRContextType {
  connection: signalR.HubConnection | null;
  isConnected: boolean;
  correlationId: string;
  generateReport: (groupId: string) => Promise<string>;
  registerListener: (callback: (job: ReportJob) => void) => void;
  unregisterListener: () => void;
  ensureRegistered: () => Promise<void>;
  connectNow: () => Promise<void>;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within SignalRProvider');
  }
  return context;
};

// localStorage (not sessionStorage) so every tab in the same browser shares one id -
// this is what lets one tab's connection register on behalf of the whole browser.
export function getOrCreateCorrelationId() {
  let id = localStorage.getItem('correlationId');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('correlationId', id);
  }
  return id;
}

interface SignalRProviderProps {
  children: ReactNode;
}

export const getReportTypeFromUrl = (url: string) => {
  try {
    const pathname = new URL(url).pathname; // /container/tenant_group_report.pdf
    const file = pathname.split('/').pop() || '';
    const base = file.replace(/\.[^/.]+$/, ''); // remove extension
    const parts = base.split('_');
    return parts.length ? parts[parts.length - 1] : 'report';
  } catch {
    return 'report';
  }
};

/**
 * NOTE:
 * Exactly one browser tab holds the live SignalR connection at a time - ownership is
 * decided with the Web Locks API, so closing that tab lets another open tab take over
 * automatically. All tabs coordinate over a BroadcastChannel:
 * - the leader tab forwards ReceiveReportStatus / connection-status events to every tab
 * - a non-leader tab that needs a guaranteed RegisterTab call (see ensureRegistered)
 *   asks the leader to do it and awaits the ack
 * window.__sharedSignalR* stays tab-local: listeners are per-tab (each tab's own React
 * components), the connection reference is only non-null on the tab that's currently leader.
 */

declare global {
  interface Window {
    __sharedSignalRConnection?: signalR.HubConnection | null;
    __sharedSignalRListeners?: Set<(job: ReportJob) => void>;
    __signalRLeaderLoopStarted?: boolean;
  }
}

export const REPORT_CHANNEL_NAME = 'signalr-report-channel';
export const CONNECTION_LOCK_NAME = 'signalr-connection-lock';
const REGISTER_TIMEOUT_MS = 5000;
export const CONNECT_NOW_TIMEOUT_MS = 10000;

type ChannelMessage =
  | { type: 'job'; job: ReportJob }
  | { type: 'status'; connected: boolean }
  | { type: 'status-request' }
  | { type: 'register-request'; requestId: string }
  | { type: 'register-ack'; requestId: string; ok: boolean; error?: string };

function requestLeaderRegister(channel: BroadcastChannel): Promise<void> {
  return new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID();
    const timeout = setTimeout(() => {
      channel.removeEventListener('message', onMessage);
      reject(new Error('Timed out waiting for SignalR leader tab to register'));
    }, REGISTER_TIMEOUT_MS);

    function onMessage(event: MessageEvent<ChannelMessage>) {
      const data = event.data;
      if (data.type === 'register-ack' && data.requestId === requestId) {
        clearTimeout(timeout);
        channel.removeEventListener('message', onMessage);
        if (data.ok) resolve();
        else reject(new Error(data.error || 'SignalR registration failed'));
      }
    }
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'register-request', requestId } satisfies ChannelMessage);
  });
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [downloadState, setDownloadState] = useState<{ isDownloading: boolean; reportType?: string }>({
    isDownloading: false,
  });
  const correlationId = getOrCreateCorrelationId();
  const { addToast } = useToast();

  // Always keep a ref to the latest access token so accessTokenFactory never serves a stale value
  const tokenRef = useRef<string | undefined>(auth?.userData?.access_token);
  useEffect(() => {
    tokenRef.current = auth?.userData?.access_token;
  }, [auth?.userData?.access_token]);

  // ensure listener set exists (per-tab; fed by the BroadcastChannel regardless of which tab leads)
  if (!window.__sharedSignalRListeners) {
    window.__sharedSignalRListeners = new Set<(job: ReportJob) => void>();
  }

  const channelRef = useRef<BroadcastChannel | null>(null);
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel(REPORT_CHANNEL_NAME);
  }

  // Fan out jobs/status coming from whichever tab is currently the leader, and answer
  // register-request on behalf of the leader connection if this tab happens to hold it.
  useEffect(() => {
    const channel = channelRef.current!;

    const onMessage = (event: MessageEvent<ChannelMessage>) => {
      const data = event.data;
      switch (data.type) {
        case 'job': {
          window.__sharedSignalRListeners?.forEach((cb) => {
            try {
              cb(data.job);
            } catch (e) {
              console.error('Error in SignalR listener callback', e);
            }
          });
          break;
        }
        case 'status': {
          setIsConnected(data.connected);
          break;
        }
        case 'status-request': {
          const conn = window.__sharedSignalRConnection;
          if (conn) {
            channel.postMessage({
              type: 'status',
              connected: conn.state === signalR.HubConnectionState.Connected,
            } satisfies ChannelMessage);
          }
          break;
        }
        case 'register-request': {
          const conn = window.__sharedSignalRConnection;
          if (!conn) return; // not the leader tab, ignore
          if (conn.state !== signalR.HubConnectionState.Connected) {
            channel.postMessage({
              type: 'register-ack',
              requestId: data.requestId,
              ok: false,
              error: 'Connection not active',
            } satisfies ChannelMessage);
            return;
          }
          conn
            .invoke('RegisterTab', correlationId)
            .then(() => channel.postMessage({ type: 'register-ack', requestId: data.requestId, ok: true } satisfies ChannelMessage))
            .catch((err) =>
              channel.postMessage({
                type: 'register-ack',
                requestId: data.requestId,
                ok: false,
                error: String(err),
              } satisfies ChannelMessage),
            );
          break;
        }
      }
    };

    channel.addEventListener('message', onMessage);
    // In case a leader already exists in another tab, learn its current status immediately.
    channel.postMessage({ type: 'status-request' } satisfies ChannelMessage);

    return () => channel.removeEventListener('message', onMessage);
  }, [correlationId]);

  // Attempts to become the browser's leader tab and hold the connection for as long as it
  // stays up. Resolves once that leadership term ends (the connection permanently closes).
  // `onConnected` fires synchronously with whether `start()` itself succeeded, so callers can
  // know the outcome of *this* attempt without waiting for the whole leadership term to end.
  const runAsLeader = useCallback(
    (onConnected?: (ok: boolean) => void) =>
      new Promise<void>((resolveLeadership) => {
        const channel = channelRef.current!;
        const hubUrl = `${Project.api}dashboardservice/reportHub`;
        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            // Use the ref so every reconnect / negotiate call gets the latest refreshed token
            accessTokenFactory: () => tokenRef.current ?? '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
          .build();

        newConnection.on('ReceiveReportStatus', (job: ReportJob) => {
          channel.postMessage({ type: 'job', job } satisfies ChannelMessage);
          window.__sharedSignalRListeners?.forEach((cb) => {
            try {
              cb(job);
            } catch (e) {
              console.error('Error in SignalR listener callback', e);
            }
          });
        });

        newConnection.onreconnecting(() => {
          setIsConnected(false);
          channel.postMessage({ type: 'status', connected: false } satisfies ChannelMessage);
          console.log('SignalR reconnecting...');
        });

        newConnection.onreconnected(async () => {
          setIsConnected(true);
          channel.postMessage({ type: 'status', connected: true } satisfies ChannelMessage);
          console.log('SignalR reconnected');
          try {
            await newConnection.invoke('RegisterTab', correlationId);
          } catch (err) {
            console.error('Error re-registering tab after reconnect:', err);
          }
        });

        newConnection.onclose((error) => {
          console.log('SignalR connection closed', error);
          setIsConnected(false);
          channel.postMessage({ type: 'status', connected: false } satisfies ChannelMessage);
          window.__sharedSignalRConnection = null;
          setConnection(null);
          resolveLeadership();
        });

        const handleUnload = () => newConnection.stop().catch(() => {});
        window.addEventListener('beforeunload', handleUnload);

        newConnection
          .start()
          .then(async () => {
            window.__sharedSignalRConnection = newConnection;
            setConnection(newConnection);
            setIsConnected(true);
            channel.postMessage({ type: 'status', connected: true } satisfies ChannelMessage);
            console.log('SignalR connected successfully (leader tab)');
            onConnected?.(true);

            try {
              await newConnection.invoke('RegisterTab', correlationId);
              console.log('Registered correlationId:', correlationId);
            } catch (err) {
              console.error('Error registering tab on start:', err);
            }
          })
          .catch((err) => {
            console.error('SignalR connection error:', err);
            window.removeEventListener('beforeunload', handleUnload);
            onConnected?.(false);
            resolveLeadership();
          });
      }),
    [correlationId],
  );

  // Compete for leadership of the single browser-wide connection via the Web Locks API.
  // Whichever tab holds the lock owns the live WebSocket; when that tab closes, the browser
  // releases the lock and the next waiting tab takes over and reconnects.
  //
  // This makes exactly ONE connection attempt on login - it does not retry in the background,
  // whether this attempt fails outright or the connection later closes for good (e.g. once
  // withAutomaticReconnect's own bounded retry policy is exhausted). A background loop that
  // keeps retrying is exactly what turns one device's brief network hiccup into a burst of
  // repeated negotiate calls, which is disproportionately expensive against a small connection
  // quota (e.g. Azure SignalR's Free tier). Instead, `connectNow()` is the only path back in:
  // report/export buttons already check `isConnected` and call it on demand right before they
  // need the connection, so a connection is only (re-)established when it's actually needed.
  useEffect(() => {
    const token = auth?.userData?.access_token;
    if (!token) {
      console.warn('No access token available for SignalR connection');
      return;
    }

    if (window.__signalRLeaderLoopStarted) return;
    window.__signalRLeaderLoopStarted = true;

    // Intentionally not tied to this effect's cleanup: React StrictMode synchronously runs
    // mount -> cleanup -> mount in dev, and the Web Locks API grants the lock asynchronously
    // afterwards - tearing the connection down on that stale cleanup would abort the very
    // connection this effect just established, leaving `isConnected` stuck false even though
    // it's connected on the wire.
    navigator.locks.request(CONNECTION_LOCK_NAME, () =>
      runAsLeader((ok) => {
        if (!ok) {
          console.warn('SignalR: initial connection attempt failed. Will connect on demand instead of retrying automatically.');
        }
      }),
    );
  }, [Boolean(auth?.userData?.access_token), runAsLeader]);

  // On-demand connect for callers (report/export buttons) that find `isConnected` false -
  // e.g. because the automatic retry loop above already gave up. Resolves immediately if a
  // connection already exists; otherwise makes exactly one attempt and awaits its outcome.
  const connectNow = useCallback((): Promise<void> => {
    if (window.__sharedSignalRConnection?.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timed out trying to connect to SignalR'));
      }, CONNECT_NOW_TIMEOUT_MS);

      navigator.locks.request(CONNECTION_LOCK_NAME, () =>
        runAsLeader((ok) => {
          clearTimeout(timeout);
          if (ok) resolve();
          else reject(new Error('Failed to connect to SignalR'));
        }),
      );
    });
  }, [runAsLeader]);

  const registerListener = useCallback((callback: (job: ReportJob) => void) => {
    window.__sharedSignalRListeners!.add(callback);
  }, []);

  // Re-invoke RegisterTab and wait for it to complete. `isConnected` flips true as soon as the
  // socket connects/reconnects, but the server only maps correlationId -> connection after
  // RegisterTab resolves. Callers that are about to kick off a report generation must await this
  // immediately beforehand, otherwise they can hit the connect/reconnect race window where the
  // socket is up but the server has no mapping yet ("No active client connection found...").
  // If this tab isn't the leader, the request is relayed to whichever tab is.
  const ensureRegistered = useCallback(async () => {
    const conn = window.__sharedSignalRConnection;
    if (conn && conn.state === signalR.HubConnectionState.Connected) {
      await conn.invoke('RegisterTab', correlationId);
      return;
    }
    await requestLeaderRegister(channelRef.current!);
  }, [correlationId]);

  const unregisterListener = useCallback(() => {
    // Consumers remove their own callback reference from window.__sharedSignalRListeners
    // directly in their effect cleanup; kept as a no-op for API parity.
  }, []);

  const generateReport = useCallback(
    async (groupId: string): Promise<string> => {
      const token = auth?.userData?.access_token;

      if (!token) throw new Error('No access token available');

      const apiUrl = `${Project.api}dashboardservice/Dashboard/Report/${selectedTenantId}${
        groupId ? `/${groupId}` : ''
      }?correlationId=${correlationId}`;

      const response = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return await response.text();
    },
    [auth?.userData?.access_token, correlationId, selectedTenantId],
  );

  const value = useMemo(
    () => ({
      connection,
      isConnected,
      correlationId,
      generateReport,
      registerListener,
      unregisterListener,
      ensureRegistered,
      connectNow,
    }),
    [connection, isConnected, correlationId, generateReport, registerListener, unregisterListener, ensureRegistered, connectNow],
  );

  // Permanent listener that saves report completion/failure to notifications + toast.
  // Gated on isConnected only - a non-leader tab has no local `connection` but still
  // receives job events over the BroadcastChannel and must still notify the user.
  useEffect(() => {
    if (!isConnected) return;

    const globalListener = (job: ReportJob) => {
      if (job.isSuccessful && job.result) {
        const message = job.message || 'Report is ready!';
        addToast(message, 'success', 4000, 'Report Ready', false);

        window.dispatchEvent(
          new CustomEvent('saveToNotification', {
            detail: {
              message,
              title: 'Report Ready',
              type: 'success',
              expirationMinutes: 30,
              meta: { downloadUrl: job.result, reportType: job.metadata?.reportType },
              correlationId: job.correlationId,
            },
          }),
        );
      } else if (!job.isSuccessful) {
        addToast(job.message || 'Report generation failed', 'error', 3000, 'Report Error', false);
        // Dispatch an event to save to notifications with download URL
        window.dispatchEvent(
          new CustomEvent('saveToNotification', {
            detail: {
              message: job.message || 'Report generation failed',
              title: 'Report Error',
              type: 'error',
              expirationMinutes: 30,
              correlationId: job.correlationId,
            },
          }),
        );
      }
    };

    // Add to shared listeners set
    window.__sharedSignalRListeners!.add(globalListener);

    return () => {
      if (window.__sharedSignalRListeners) {
        window.__sharedSignalRListeners.delete(globalListener);
      }
    };
  }, [isConnected, addToast]);

  const getReportTypeFromUrl = (url: string) => {
    try {
      const pathname = new URL(url).pathname;
      const file = pathname.split('/').pop() || '';
      const base = file.replace(/\.[^/.]+$/, '');
      const parts = base.split('_');
      return parts.length ? parts[parts.length - 1] : 'report';
    } catch {
      return 'report';
    }
  };

  const downloadReport = useCallback(
    (url: string, reportType?: string) => {
      if (downloadState.isDownloading) {
        addToast('Another report download is in progress. Please wait…', 'info', 3000, 'Download In Progress');
        return;
      }

      const resolvedType = reportType || getReportTypeFromUrl(url);
      setDownloadState({ isDownloading: true, reportType: resolvedType });

      // Convert slashes inside reportId to encoded %2F
      const fetchReportIndex = url.indexOf('FetchReport/');

      if (fetchReportIndex !== -1) {
        const basePart = url.substring(0, fetchReportIndex + 'FetchReport/'.length);
        const reportIdPart = url.substring(fetchReportIndex + 'FetchReport/'.length);

        const encodedReportId = encodeURIComponent(reportIdPart);

        url = basePart + encodedReportId;
      }

      api
        .get(url, { responseType: 'blob' })
        .then((response) => {
          let fileName = `${resolvedType}_report.pdf`;
          const contentType = response.headers['content-type'];
          let extension = 'pdf';

          // Try to get filename from Content-Disposition
          const disposition = response.headers['content-disposition'];
          if (disposition && disposition.includes('filename=')) {
            fileName = disposition.split('filename=')[1].split(';')[0].replace(/"/g, '').trim();
          } else if (contentType) {
            if (contentType.includes('zip')) extension = 'zip';
            else if (contentType.includes('csv')) extension = 'csv';
            else if (contentType.includes('pdf')) extension = 'pdf';
            else if (contentType.includes('excel')) extension = 'xlsx';
            fileName = `${resolvedType}_report.${extension}`;
          }

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);

          addToast('Report downloaded successfully!', 'success', 3000, 'Download Complete');
        })
        .catch((error) => {
          console.error('Error downloading report:', error);
          addToast('Failed to download report', 'error', 3000, 'Download Error');
        })
        .finally(() => {
          setDownloadState({ isDownloading: false });
        });
    },
    [addToast, downloadState.isDownloading],
  );

  // Listen for performDownload event and trigger downloadReport
  useEffect(() => {
    const handlePerformDownload = (event: Event) => {
      const customEvent = event as CustomEvent<{ url: string; reportType?: string }>;
      const { url, reportType } = customEvent.detail || {};
      if (!url) return;
      downloadReport(url, reportType);
    };

    window.addEventListener('performDownload', handlePerformDownload);
    return () => window.removeEventListener('performDownload', handlePerformDownload);
  }, [downloadReport]);

  return (
    <SignalRContext.Provider value={value}>
      {children}
      <DownloadStatus isDownloading={downloadState.isDownloading} reportType={downloadState.reportType} />
    </SignalRContext.Provider>
  );
};
